/**
 * ApprovalsView — typed Approval workflow on the active project.
 *
 * Lists every typed :Approval node grouped by state (draft / pending /
 * approved / rejected), persona-fronted by Dev Kapoor (the
 * approval-age agent). Surfaces SKUs that need an APPROVED edge but
 * don't have one — driven by the same threshold logic
 * detectMissingApproval (lib/agent/agentSuggestions.ts) uses, so the
 * UI and the rule fires never disagree.
 *
 * Age comes from the prov:atTime helper (lib/temporal.ts) so editing
 * the seed graph or letting wall-clock advance changes the chip
 * automatically. Nothing here is a static count.
 */
import { useEffect, useMemo, useState } from "react";
import {
  ShieldCheck, ShieldAlert, Clock4, Send, CheckCircle2, XCircle, Hourglass, AlertTriangle, IndianRupee,
} from "lucide-react";
import { useActiveProjectGraph } from "../../lib/useActiveProjectGraph";
import { mergeProjectLayers } from "../../lib/projectLayers";
import type { GraphNode, ProjectGraph } from "../../types/ontology";
import { formatCompactMoney } from "../../lib/i18n/format";
import { ageDays, relativeFromNode } from "../../lib/temporal";
import { resolveActor } from "../../lib/agent/timeline";
import { env } from "../../lib/env";

type ApprovalState = "draft" | "pending" | "approved" | "rejected";
const STATE_ORDER: ApprovalState[] = ["pending", "draft", "approved", "rejected"];

interface StateConfig {
  readonly label: string;
  readonly icon: JSX.Element;
  readonly chipCls: string;
}
const STATE_META: Readonly<Record<ApprovalState, StateConfig>> = {
  pending:  { label: "Pending",  icon: <Hourglass className="w-3 h-3" />, chipCls: "bg-foyr/10 border-foyr/40 text-foyr-deep" },
  draft:    { label: "Draft",    icon: <Send className="w-3 h-3" />,      chipCls: "bg-cream-dark border-border text-ink-700" },
  approved: { label: "Approved", icon: <CheckCircle2 className="w-3 h-3" />, chipCls: "bg-fern/10 border-fern/40 text-fern" },
  rejected: { label: "Rejected", icon: <XCircle className="w-3 h-3" />,    chipCls: "bg-foyr-deep/10 border-foyr-deep/40 text-foyr-deep" },
};

function attrStr(n: GraphNode, key: string): string | null {
  const v = n.attrs[key];
  return typeof v === "string" ? v : null;
}
function attrNum(n: GraphNode, key: string): number | null {
  const v = n.attrs[key];
  return typeof v === "number" && Number.isFinite(v) ? v : null;
}
function stateOf(a: GraphNode): ApprovalState {
  const s = (attrStr(a, "state") ?? "pending").toLowerCase();
  if (s === "draft" || s === "approved" || s === "rejected" || s === "pending") return s;
  return "pending";
}

interface MissingApproval {
  readonly sku: GraphNode;
  readonly roomLabel: string | null;
  readonly price: number;
}

/** SKUs whose price >= the configured high-value threshold but which
 *  have no APPROVED edge. Same predicate detectMissingApproval uses,
 *  so the UI list and the typed rule fires stay in sync. */
function findHighValueWithoutApproval(graph: ProjectGraph): ReadonlyArray<MissingApproval> {
  const threshold = env.VITE_HIGH_VALUE_THRESHOLD_INR;
  const result: MissingApproval[] = [];
  for (const sku of graph.nodes) {
    if (sku.kind !== "SKU") continue;
    const price = attrNum(sku, "price") ?? 0;
    if (price < threshold) continue;
    const hasApproval = graph.edges.some(
      (e) => (e.kind === "APPROVED" || e.kind === "APPROVED_BY") && (e.source === sku.id || e.target === sku.id),
    );
    if (hasApproval) continue;
    const roomEdge = graph.edges.find((e) => e.kind === "IN_ROOM" && e.source === sku.id);
    const room = roomEdge ? graph.nodes.find((n) => n.id === roomEdge.target && n.kind === "Room") : undefined;
    result.push({ sku, roomLabel: room?.label ?? null, price });
  }
  return result;
}

export function ApprovalsView(): JSX.Element {
  const { graph: baseGraph, activeId } = useActiveProjectGraph();
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const bump = (): void => setTick((t) => t + 1);
    window.addEventListener("foyrmind:timeline", bump);
    window.addEventListener("foyrmind:projectChanged", bump);
    return () => {
      window.removeEventListener("foyrmind:timeline", bump);
      window.removeEventListener("foyrmind:projectChanged", bump);
    };
  }, []);
  const graph = useMemo(() => { void tick; return mergeProjectLayers(baseGraph, activeId); }, [baseGraph, activeId, tick]);

  const approvalsByState = useMemo(() => {
    const m: Record<ApprovalState, GraphNode[]> = { pending: [], draft: [], approved: [], rejected: [] };
    for (const n of graph.nodes) {
      if (n.kind !== "Approval") continue;
      m[stateOf(n)].push(n);
    }
    return m;
  }, [graph.nodes]);

  const missing = useMemo(() => findHighValueWithoutApproval(graph), [graph]);
  const dev = resolveActor("agent", "approval-age");

  const total = STATE_ORDER.reduce((s, st) => s + approvalsByState[st].length, 0);
  const overdueDays = env.VITE_APPROVAL_AGE_DAYS;
  const overdue = approvalsByState.pending.filter((a) => ageDays(a) >= overdueDays).length;

  return (
    <section className="space-y-3">
      <header className="rounded-2xl border border-border/70 bg-cream px-4 py-3 flex items-start gap-3">
        <div
          className="shrink-0 w-10 h-10 rounded-full grid place-items-center text-[12px] font-semibold text-cream"
          style={{ background: dev.color }}
          title={`${dev.displayName} — ${dev.title}`}
        >
          {dev.initials}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[11px] uppercase tracking-[0.14em] text-ink-500 inline-flex items-center gap-1.5">
            <ShieldCheck className="w-3 h-3" /> Approvals · {graph.projectName}
          </div>
          <h3 className="font-newyork text-[22px] text-ink-950 leading-tight mt-0.5">
            {dev.displayName} is watching {total} typed approval{total === 1 ? "" : "s"}
            {missing.length > 0 ? ` · ${missing.length} SKU${missing.length === 1 ? "" : "s"} need one` : ""}
          </h3>
          <div className="flex flex-wrap gap-1.5 mt-1.5 text-[11px] tabular">
            {STATE_ORDER.map((st) => approvalsByState[st].length > 0 ? (
              <span key={st} className={"pill border inline-flex items-center gap-1 " + STATE_META[st].chipCls}>
                {STATE_META[st].icon}{approvalsByState[st].length} {STATE_META[st].label.toLowerCase()}
              </span>
            ) : null)}
            {overdue > 0 ? (
              <span className="pill border border-foyr-deep/40 bg-foyr-deep/10 text-foyr-deep inline-flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" />{overdue} pending {overdueDays}d+
              </span>
            ) : null}
          </div>
        </div>
      </header>

      {/* Missing approvals — high-value SKUs without APPROVED edge */}
      {missing.length > 0 ? (
        <section className="rounded-2xl border border-foyr/40 bg-foyr/5 p-3 space-y-1.5">
          <div className="text-[11px] uppercase tracking-[0.14em] text-foyr-deep inline-flex items-center gap-1.5 px-1">
            <ShieldAlert className="w-3 h-3" /> {missing.length} high-value SKU{missing.length === 1 ? "" : "s"} need a typed Approval
          </div>
          <div className="text-[11px] text-ink-700 px-1">
            Threshold {formatCompactMoney(env.VITE_HIGH_VALUE_THRESHOLD_INR)} · same predicate <code className="px-1 bg-cream-dark rounded text-[10px]">detectMissingApproval</code> uses.
          </div>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 mt-1">
            {missing.map((m) => (
              <li key={m.sku.id} className="rounded-xl border border-border bg-cream px-3 py-2 flex items-center justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="text-[12px] text-ink-950 truncate">{m.sku.label}</div>
                  <div className="text-[10.5px] text-ink-500 tabular">{m.roomLabel ?? "Unassigned"}</div>
                </div>
                <div className="shrink-0 text-right">
                  <div className="text-[11.5px] tabular font-medium text-ink-950 inline-flex items-center gap-0.5"><IndianRupee className="w-3 h-3 text-foyr" />{formatCompactMoney(m.price)}</div>
                </div>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {/* Typed Approval nodes — grouped by state */}
      {STATE_ORDER.map((st) => {
        const items = approvalsByState[st];
        if (items.length === 0) return null;
        const cfg = STATE_META[st];
        return (
          <section key={st} className="panel p-3 space-y-2">
            <header className="flex items-center gap-2 px-1">
              <span className={"pill border inline-flex items-center gap-1 " + cfg.chipCls}>
                {cfg.icon}{cfg.label}
              </span>
              <span className="text-[10.5px] text-ink-500 tabular">{items.length}</span>
            </header>
            <ul className="space-y-1.5">
              {items.map((a) => {
                const role = attrStr(a, "role");
                const isOverdue = st === "pending" && ageDays(a) >= overdueDays;
                return (
                  <li key={a.id} className={"rounded-xl border bg-cream px-3 py-2 flex items-center gap-3 " + (isOverdue ? "border-foyr-deep/40 bg-foyr-deep/5" : "border-border")}>
                    <div className="flex-1 min-w-0">
                      <div className="text-[12.5px] text-ink-950 leading-tight">{a.label}</div>
                      <div className="text-[10.5px] text-ink-500 tabular mt-0.5 flex flex-wrap gap-1.5">
                        {role ? <span>role · {role}</span> : null}
                        <span className="inline-flex items-center gap-0.5"><Clock4 className="w-2.5 h-2.5" />{relativeFromNode(a)}</span>
                        {isOverdue ? <span className="text-foyr-deep font-medium">overdue</span> : null}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          </section>
        );
      })}

      {total === 0 && missing.length === 0 ? (
        <section className="panel p-8 text-center text-[12.5px] text-ink-500">
          <CheckCircle2 className="w-5 h-5 mx-auto mb-1.5 text-fern" />
          {graph.projectName} has no typed Approvals and no high-value SKUs flagged. Clean.
        </section>
      ) : null}
    </section>
  );
}
